<template>
    <div>

        <Head :title="__('Dashboard')" />

        <!-- Ticket Stats Section -->
        <div class="badge__items flex flex-col lg:flex-row gap-5">
            <div class="badge__item h-32 w-full lg:w-1/4 cursor-pointer"
                @click="goToLink(this.route('tickets', { 'type': 'new' }))">
                <div class="l__items bg-white rounded-lg shadow-lg flex justify-between w-full">
                    <div class="badge__info">
                        <span class="title">{{ __('New Tickets') }}</span>
                        <span class="number">{{ new_tickets }}</span>
                    </div>
                    <div class="a__right">
                        <div class="round_circle pr-3 rtl:pl-3">
                            <div class="pie animate"
                                :style="{ '--pie_val': parseInt((new_tickets * 100) / total_tickets) }"> {{
                                    parseInt((new_tickets * 100) / total_tickets) || 0 }}%</div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="badge__item h-32 w-full lg:w-1/4 cursor-pointer"
                @click="goToLink(this.route('tickets', { 'type': 'open' }))">
                <div class="l__items bg-white rounded-lg shadow-lg flex justify-between w-full">
                    <div class="badge__info">
                        <span class="title">{{ __('Open Tickets') }}</span>
                        <span class="number">{{ opened_tickets }}</span>
                    </div>
                    <div class="a__right">
                        <div class="round_circle pr-3 rtl:pl-3">
                            <div class="pie animate"
                                :style="{ '--pie_val': parseInt((opened_tickets * 100) / total_tickets) }"> {{
                                    parseInt((opened_tickets * 100) / total_tickets) || 0 }}%</div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="badge__item h-32 w-full lg:w-1/4 cursor-pointer"
                @click="goToLink(this.route('tickets', { 'search': 'close' }))">
                <div class="l__items bg-white rounded-lg shadow-lg flex justify-between w-full">
                    <div class="badge__info">
                        <span class="title">{{ __('Closed Tickets') }}</span>
                        <span class="number">{{ closed_tickets }}</span>
                    </div>
                    <div class="a__right">
                        <div class="round_circle pr-3 rtl:pl-3">
                            <div class="pie animate"
                                :style="{ '--pie_val': parseInt((closed_tickets * 100) / total_tickets) }"> {{
                                    parseInt((closed_tickets * 100) / total_tickets) || 0 }}%</div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="auth.user.role.slug !== 'customer'" class="badge__item h-32 w-70 lg:w-1/4 cursor-pointer"
                @click="goToLink(this.route('tickets', { 'type': 'un_assigned' }))">
                <div class="l__items bg-white rounded-lg shadow-lg flex justify-between w-full">
                    <div class="badge__info">
                        <span class="title">{{ __('Unassigned Tickets') }}</span>
                        <span class="number">{{ un_assigned_tickets }}</span>
                    </div>
                    <div class="a__right">
                        <div class="round_circle pr-3 rtl:pl-3">
                            <div class="pie animate"
                                :style="{ '--pie_val': parseInt((un_assigned_tickets * 100) / total_tickets) }"> {{
                                    parseInt((un_assigned_tickets * 100) / total_tickets) || 0 }}%</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Ticket Sources Section -->
        <div class="mt-8" v-if="auth.user.role.slug !== 'customer'">
            <div class="bg-white rounded-lg shadow-lg p-5">
                <h2 class="font-bold text-lg mb-4">{{ __('Ticket Sources') }}</h2>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div v-for="(source, index) in top_sources" :key="index"
                        class="border rounded-lg p-4 flex items-center"
                        :style="{ 'border-left': `4px solid ${source.color}` }">
                        <div class="ml-3">
                            <h3 class="font-medium text-gray-900">{{ source.name }}</h3>
                            <p class="text-gray-500">{{ source.count }} tickets</p>
                            <div class="w-full bg-gray-200 rounded-full h-2 mt-2">
                                <div class="h-2 rounded-full" :style="{
                                    'width': `${(source.count / count_tickets_by_source) * 100}%`,
                                    'background-color': source.color
                                }"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Chat Ratings Section - FIXED -->
        <div class="mt-8">
            <div class="bg-white rounded-lg shadow-lg p-5">
                <h2 class="font-bold text-lg mb-4">{{ __('Chat Ratings') }}</h2>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <!-- Average Rating -->
                    <div class="border rounded-lg p-4 flex items-center border-l-4 border-blue-500">
                        <div class="ml-3">
                            <h3 class="font-medium text-gray-900">{{ __('Average Rating') }}</h3>
                            <p class="text-2xl font-bold">
                                {{ average_chat_rating ? parseFloat(average_chat_rating).toFixed(1) : '0.0' }}
                                <span class="text-sm text-gray-500">/ 5</span>
                            </p>
                            <div class="flex items-center mt-2">
                                <span class="text-yellow-400 mr-1" v-for="star in 5" :key="'avg-'+star">
                                    <icon v-if="star <= Math.round(average_chat_rating || 0)" name="star" class="h-5 w-5 fill-current" />
                                    <icon v-else name="star" class="h-5 w-5 text-gray-300" />
                                </span>
                            </div>
                        </div>
                    </div>

                <!-- Rating Distribution -->
<div class="border rounded-lg p-4 flex flex-col border-l-4 border-green-500">
    <h3 class="font-medium text-gray-900 mb-2">{{ __('Rating Distribution') }}</h3>
    <div v-if="chat_rating_distribution && total_chat_ratings > 0">
        <div v-for="rating in [5, 4, 3, 2, 1]" :key="'dist-'+rating" class="mb-1">
            <div class="flex items-center">
                <span class="w-8 flex items-center">
                    <span class="mr-1">{{ rating }}</span>
                    <span class="text-yellow-400">★</span>
                </span>
                <div class="w-full bg-gray-200 rounded-full h-2.5 mx-2">
                    <div class="bg-green-500 h-2.5 rounded-full"
                         :style="{ width: `${((chat_rating_distribution[rating] || 0) / total_chat_ratings) * 100}%` }"></div>
                </div>
                <span class="text-sm text-gray-600">{{ chat_rating_distribution[rating] || 0 }}</span>
            </div>
        </div>
    </div>
    <div v-else class="text-sm text-gray-500">{{ __('No ratings yet') }}</div>
</div>

                 <!-- Recent Ratings -->
                   <!-- Recent Ratings -->
                        <div class="border rounded-lg p-4 border-l-4 border-purple-500">
                            <h3 class="font-medium text-gray-900 mb-2">{{ __('Recent Ratings') }}</h3>
                            <div v-if="recent_chat_ratings && recent_chat_ratings.length" class="max-h-32 overflow-y-auto">
                                <div v-for="rating in recent_chat_ratings" :key="rating.id" class="mb-3 pb-2 border-b">
                                    <div class="flex items-center">
                                        <span class="flex items-center">
                                            <span class="text-yellow-400 mr-1 flex">
                                                <template v-for="star in 5" :key="'recent-'+rating.id+'-'+star">
                                                    <span v-if="star <= rating.rating" class="text-yellow-400">★</span>
                                                    <span v-else class="text-gray-300">★</span>
                                                </template>
                                            </span>
                                            <span class="text-sm text-gray-700 ml-1">{{ rating.rating }}/5</span>
                                        </span>
                                        <span class="text-xs text-gray-500 ml-2">
                                            • {{ formatDate(rating.rated_at) }}
                                        </span>
                                    </div>
                                    <p class="text-sm text-gray-700 mt-1" v-if="rating.comment">
                                        {{ rating.comment }}
                                    </p>
                                </div>
                            </div>
                            <p v-else class="text-sm text-gray-500">{{ __('No recent ratings') }}</p>
                        </div>
                </div>
            </div>
        </div>

        <!-- Ticket Reviews Section -->
<div class="mt-8">
    <div class="bg-white rounded-lg shadow-lg p-5">
        <h2 class="font-bold text-lg mb-4">{{ __('Ticket Reviews') }}</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <!-- Average Rating -->
            <div class="border rounded-lg p-4 flex items-center border-l-4 border-blue-500">
                <div class="ml-3">
                    <h3 class="font-medium text-gray-900">{{ __('Average Rating') }}</h3>
                    <p class="text-2xl font-bold">
                        {{ average_ticket_rating ? parseFloat(average_ticket_rating).toFixed(1) : '0.0' }}
                        <span class="text-sm text-gray-500">/ 5</span>
                    </p>
                    <div class="flex items-center mt-2">
                        <span class="text-yellow-400 mr-1" v-for="star in 5" :key="'ticket-avg-'+star">
                            <icon v-if="star <= Math.round(average_ticket_rating || 0)" name="star" class="h-5 w-5 fill-current" />
                            <icon v-else name="star" class="h-5 w-5 text-gray-300" />
                        </span>
                    </div>
                </div>
            </div>

            <!-- Rating Distribution -->
            <div class="border rounded-lg p-4 flex flex-col border-l-4 border-green-500">
                <h3 class="font-medium text-gray-900 mb-2">{{ __('Rating Distribution') }}</h3>
                <div v-if="ticket_rating_distribution && total_ticket_reviews > 0">
                    <div v-for="rating in [5, 4, 3, 2, 1]" :key="'ticket-dist-'+rating" class="mb-1">
                        <div class="flex items-center">
                            <span class="w-8 flex items-center">
                                <span class="mr-1">{{ rating }}</span>
                                <span class="text-yellow-400">★</span>
                            </span>
                            <div class="w-full bg-gray-200 rounded-full h-2.5 mx-2">
                                <div class="bg-green-500 h-2.5 rounded-full"
                                     :style="{ width: `${((ticket_rating_distribution[rating] || 0) / total_ticket_reviews) * 100}%` }"></div>
                            </div>
                            <span class="text-sm text-gray-600">{{ ticket_rating_distribution[rating] || 0 }}</span>
                        </div>
                    </div>
                </div>
                <div v-else class="text-sm text-gray-500">{{ __('No reviews yet') }}</div>
            </div>

            <!-- Recent Reviews -->
            <div class="border rounded-lg p-4 border-l-4 border-purple-500">
                <h3 class="font-medium text-gray-900 mb-2">{{ __('Recent Reviews') }}</h3>
                <div v-if="recent_ticket_reviews && recent_ticket_reviews.length" class="max-h-32 overflow-y-auto">
                    <div v-for="review in recent_ticket_reviews" :key="review.id" class="mb-3 pb-2 border-b">
                        <div class="flex items-center">
                            <span class="flex items-center">
                                <span class="text-yellow-400 mr-1 flex">
                                    <template v-for="star in 5" :key="'review-'+review.id+'-'+star">
                                        <span v-if="star <= review.rating" class="text-yellow-400">★</span>
                                        <span v-else class="text-gray-300">★</span>
                                    </template>
                                </span>
                                <span class="text-sm text-gray-700 ml-1">{{ review.rating }}/5</span>
                            </span>
                            <span class="text-xs text-gray-500 ml-2">
                                • {{ formatDate(review.created_at) }}
                            </span>
                        </div>
                        <p class="text-sm text-gray-700 mt-1" v-if="review.comment">
                            {{ review.comment }}
                        </p>
                        <p class="text-xs text-gray-500 mt-1" v-if="review.ticket">
                            Ticket: {{ review.ticket.subject }}
                        </p>
                        <p class="text-xs text-gray-500" v-if="review.user">
                            By: {{ review.user.name }}
                        </p>
                    </div>
                </div>
                <p v-else class="text-sm text-gray-500">{{ __('No recent reviews') }}</p>
            </div>
        </div>
    </div>
</div>

        <div class="response__details mt-8 flex gap-5 flex-col lg:flex-row" v-if="auth.user.role.slug !== 'customer'">
            <div class="w-full">
                <div class="r__wrapper flex flex-col pl-5 pr-5 pb-5 bg-white items-center rounded-lg shadow-lg rd">
                    <h2 class="th__ttl font-bold text-lg pt-3">{{ __('Ticket by department') }}</h2>
                    <div class="th__info flex flex-col pt-3">
                        <vc-donut background="white" foreground="grey" :size="120" unit="px" :thickness="45" has-legend
                            legend-placement="right" :sections="top_departments" :total="100" :start-angle="0"
                            :auto-adjust-text-size="true">
                        </vc-donut>
                    </div>
                </div>
            </div>
            <div class="w-full">
                <div class="r__wrapper flex flex-col pl-5 pr-5 pb-5 bg-white items-center rounded-lg shadow-lg rd">
                    <h2 class="th__ttl font-bold text-lg pt-3">{{ __('Ticket by type') }}</h2>
                    <div class="th__info flex flex-col pt-3">
                        <vc-donut background="white" foreground="grey" :size="120" unit="px" :thickness="45" has-legend
                            legend-placement="right" :sections="top_types" :total="100" :start-angle="0"
                            :auto-adjust-text-size="true">
                        </vc-donut>
                    </div>
                </div>
            </div>
            <div class="w-full">
                <div class="r__wrapper flex flex-col pl-5 pr-5 pb-5 bg-white items-center rounded-lg shadow-lg rd">
                    <h2 class="th__ttl font-bold text-lg pt-3">{{ __('Top ticket creator') }}</h2>
                    <div class="th__info flex flex-col pt-3">
                        <vc-donut background="white" foreground="grey" :size="120" unit="px" :thickness="45" has-legend
                            legend-placement="right" :sections="top_creators" :total="100" :start-angle="0"
                            :auto-adjust-text-size="true">
                        </vc-donut>
                    </div>
                </div>
            </div>
        </div>

        <div class="response__details flex flex-col lg:flex-row mt-8 gap-5" v-if="auth.user.role.slug !== 'customer'">
            <div class="flex gap-5 flex-col lg:flex-row lg:w-10/12">
                <div class="w-full">
                    <div class="r__wrapper flex flex-col p-5 bg-white rounded-lg shadow-lg rd">
                        <h2 class="th__ttl font-bold text-lg pt-3">{{ __('Ticket history') }}</h2>
                        <div class="th__info flex">
                            <span class="text-2xl font-bold"> {{ chart_line.this_month }} </span>
                            <span class="pt-2 text-xs pl-1 pr-1">/ {{ __('this month') }}</span>
                            <span class="pt-2 text-xs font-bold pl-2 pr-2">{{ __('last month') }} {{
                                chart_line.last_month }}</span>
                        </div>
                        <div v-if="months.length" class="flex w-full justify-center c__wrapper ">
                            <div class="c__line flex flex-col w-full items-center gap-3" v-for="cl in months">
                                <span class="cl__b" :style="{ '--cl_value': cl.value }"></span>
                                <span class="cl__n text-xs">{{ cl.month }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class=" rd w-full lg:w-2/5">
                    <div class="r__wrapper flex flex-col pl-5 pr-5 pb-5 bg-white rounded-lg shadow-lg rd">
                        <div class="res__info">
                            <span class="title">{{ __('First Response Time') }}</span>
                            <span class="res_avg">{{ __('Average') }}</span>
                            <div class="flex justify-start">
                                <div v-if="firstResponse.length" v-for="(fr, fri) in firstResponse"
                                    class="res_time pr-5" :key="fri">
                                    <span class="num">{{ fr[0] }}</span>
                                    <span class="text">{{ __(fr[1]) }}</span>
                                </div>
                                <div v-else class="res_time pr-5">
                                    0
                                </div>
                            </div>
                        </div>
                        <div class="tc__info">
                            <span class="title">{{ __('Last Response Time') }}</span>
                            <span class="res_avg">{{ __('Average') }}</span>
                            <div class="flex justify-start">
                                <div v-if="lastResponse.length" v-for="(fr, fri) in lastResponse" class="res_time pr-5"
                                    :key="fri">
                                    <span class="num">{{ fr[0] }}</span>
                                    <span class="text">{{ __(fr[1]) }}</span>
                                </div>
                                <div v-else class="res_time pr-5">
                                    0
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="flex gap-5 flex-col lg:w-2/12">
                <div class="badge__item h-32 w-full cursor-pointer" @click="goToLink(this.route('users'))">
                    <div class="l__items bg-white rounded-lg shadow-lg flex justify-between w-full">
                        <div class="badge__info">
                            <span class="title">{{ __('Customers') }}</span>
                            <span class="number">{{ total_customer }}</span>
                        </div>
                        <div class="a__right">
                            <icon name="pending_users" class="h-5 fill-gray-400 mr-5" />
                        </div>
                    </div>
                </div>
                <div class="badge__item h-32 w-full cursor-pointer" @click="goToLink(this.route('contacts'))">
                    <div class="l__items bg-white rounded-lg shadow-lg flex justify-between w-full">
                        <div class="badge__info">
                            <span class="title">{{ __('Contacts') }}</span>
                            <span class="number">{{ total_contacts }}</span>
                        </div>
                        <div class="a__right">
                            <icon name="contact" class="h-5 fill-gray-400 mr-5 ml-5" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Loading Process -->
        <div v-if="loading" class="processing-overlay">
            <div class="background"></div>
            <div class="loader">
                <svg width="200px" height="200px" xmlns="http://www.w3.org/2000/svg"
                    xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid"
                    style="background: none;">
                    <circle cx="75" cy="50" fill="#ffffff" r="6.39718">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.875s"></animate>
                    </circle>
                    <circle cx="67.678" cy="67.678" fill="#ffffff" r="4.8">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.75s"></animate>
                    </circle>
                    <circle cx="50" cy="75" fill="#ffffff" r="4.8">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.625s"></animate>
                    </circle>
                    <circle cx="32.322" cy="67.678" fill="#ffffff" r="4.8">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.5s"></animate>
                    </circle>
                    <circle cx="25" cy="50" fill="#ffffff" r="4.8">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.375s"></animate>
                    </circle>
                    <circle cx="32.322" cy="32.322" fill="#ffffff" r="4.80282">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.25s"></animate>
                    </circle>
                    <circle cx="50" cy="25" fill="#ffffff" r="6.40282">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="-0.125s"></animate>
                    </circle>
                    <circle cx="67.678" cy="32.322" fill="#ffffff" r="7.99718">
                        <animate attributeName="r" values="4.8;4.8;8;4.8;4.8" times="0;0.1;0.2;0.3;1" dur="1s"
                            repeatCount="indefinite" begin="0s"></animate>
                    </circle>
                </svg>
            </div>
        </div>
    </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import Icon from '@/Shared/Icon'

export default {
    metaInfo: { title: 'Dashboard' },
    components: {
        Head,
        Icon,
        Link,
    },
    layout: Layout,
    props: {
        auth: Object,
        entries: Array,
        chart_line: Object,
        api_key: String,
        new_tickets: Number,
        total_tickets: Number,
        un_assigned_tickets: Number,
        opened_tickets: Number,
        top_sources: Array,
        count_tickets_by_source: Number,
        closed_tickets: Number,
        first_response: Array,
        top_creators: Array,
        last_response: Array,
        top_departments: Array,
        top_types: Array,
        total_customer: Number,
        total_contacts: Number,
        // Fixed chat rating props
        average_chat_rating: Number,
        chat_rating_distribution: Object,
        total_chat_ratings: Number,
        recent_chat_ratings: Array,
        first_response_time: Array,
        last_response_time: Array,
        // Ticket reviews props
         average_ticket_rating: Number,
    ticket_rating_distribution: Object,
    total_ticket_reviews: Number,
    recent_ticket_reviews: Array,
    },
    data() {
        return {
            errors: [],
            loading: false,
            firstResponse: [],
            lastResponse: [],
            months: [],
        }
    },
    created() {
        for (let i = 0; i < this.first_response.length; i++) {
            if (i % 2 === 0) {
                this.firstResponse = [...this.firstResponse, [this.first_response[i], this.first_response[i + 1]]]
            }
        }
        for (let i = 0; i < this.last_response.length; i++) {
            if (i % 2 === 0) {
                this.lastResponse = [...this.lastResponse, [this.last_response[i], this.last_response[i + 1]]]
            }
        }

        this.months = this.chart_line.previousMonths.map(m => {
            return { 'month': m, 'value': this.chart_line.months[m] ? ((this.chart_line.months[m] * 100) / this.chart_line.total) + '%' : '0%' }
        })
    },
    methods: {
        goToLink(link) {
            window.location.href = link;
        },
        formatDate(dateString) {
            const options = { year: 'numeric', month: 'short', day: 'numeric' };
            return new Date(dateString).toLocaleDateString(undefined, options);
        }
    },
}
</script>

<style scoped>
/* Existing styles from original template */
.badge__items {
    margin-bottom: 1.5rem;
}

.l__items {
    height: 100%;
}

.badge__info {
    display: flex;
    flex-direction: column;
}

.badge__info .title {
    font-size: 0.875rem;
    color: #6b7280;
    margin-bottom: 0.5rem;
}

.badge__info .number {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
}

.round_circle {
    display: flex;
    align-items: center;
}

.pie {
    --size: 3.5rem;
    --pie_val: 0;
    width: var(--size);
    border-radius: 50%;
    background: conic-gradient(#3b82f6 calc(var(--pie_val) * 1%), #e5e7eb 0);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 600;
    color: #111827;
}
</style>
